/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  DropdownList,
  DropdownUserAssistance,
  EmptyResults,
  LinkItem,
  SelectMobileDropdown,
  preventDefault
} from '#PRIVATE_SelectCommon';
import type { DataState } from '#UNSAFE_Collection';
import { Label } from '#UNSAFE_Label';
import { Separator } from '#UNSAFE_Separator';
import { InlineUserAssistance, type UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import { View } from '#UNSAFE_View';
import { FormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useTextField } from '#hooks/UNSAFE_useTextField';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '#resources/nls/bundle';
import type { ComponentChildren, ComponentProps } from 'preact';
import type { CollectionRendererProps } from './SelectSingle.types';
import { SelectSingleField } from './SelectSingleField';
import { SelectSingleFieldInput } from './SelectSingleFieldInput';
import { useMobileDropdown, type UseMobileDropdownProps } from './useMobileDropdown';

type SelectSingleFieldInputProps = ComponentProps<typeof SelectSingleFieldInput>;

type PickedInlineUserAssistanceProps = Pick<
  ComponentProps<typeof InlineUserAssistance>,
  'assistiveText' | 'helpSourceLink' | 'helpSourceText'
>;
type PickedSelectSingleFieldInputProps = Pick<
  ComponentProps<typeof SelectSingleFieldInput>,
  | 'aria-describedby'
  | 'aria-label'
  | 'displayValue'
  | 'isAddToListShown'
  | 'isAdvancedSearchShown'
  | 'isRequired'
  | 'placeholder'
  | 'textAlign'
  | 'virtualKeyboard'
> & {
  onFieldBlur?: ComponentProps<typeof SelectSingleFieldInput>['onBlur'];
  onFieldFocus?: ComponentProps<typeof SelectSingleFieldInput>['onFocus'];
  onFieldInput?: ComponentProps<typeof SelectSingleFieldInput>['onInput'];
  onFieldKeyDown?: ComponentProps<typeof SelectSingleFieldInput>['onKeyDown'];
  onFieldKeyUp?: ComponentProps<typeof SelectSingleFieldInput>['onKeyUp'];
};
type PickedSelectMobileDropdownProps = Pick<ComponentProps<typeof SelectMobileDropdown>, 'isOpen'>;
type PickedUseMobileDropdownProps<K extends string | number, D extends Record<string, any>> = Pick<
  UseMobileDropdownProps<K, D>,
  'clearValue' | 'setDropdownOpen' | 'stopFiltering' | 'setUserInput'
> & {
  mainFieldInputRef: UseMobileDropdownProps<K, D>['inputRef'];
};

type Props<
  K extends string | number,
  D extends Record<string, any>
> = PickedInlineUserAssistanceProps &
  PickedSelectMobileDropdownProps &
  PickedSelectSingleFieldInputProps &
  PickedUseMobileDropdownProps<K, D> & {
    data?: DataState<K, D> | null;
    collectionRenderer: (props: CollectionRendererProps<K, D>) => ComponentChildren;
    collectionRendererContext: CollectionRendererProps<K, D>;
    dropdownId: string;
    isEmptyResults: boolean;
    isFocused: boolean;
    isLoading: boolean;
    label: string;
    liveRegionText?: string;
    onAddToListAction?: () => void;
    onAdvancedSearchAction?: () => void;
    propIsLoading?: boolean;
    userAssistanceDensity?: UserAssistanceDensityType;
    userInput?: string;
    virtualKeyboard?: SelectSingleFieldInputProps['virtualKeyboard'];
  };

export function SelectSingleMobileDropdown<
  K extends string | number,
  D extends Record<string, any>
>({
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  assistiveText,
  clearValue,
  collectionRenderer,
  collectionRendererContext,
  data,
  displayValue,
  dropdownId,
  helpSourceLink,
  helpSourceText,
  isAddToListShown,
  isAdvancedSearchShown,
  isEmptyResults,
  isFocused,
  isLoading,
  isOpen = false,
  isRequired,
  label,
  liveRegionText,
  mainFieldInputRef,
  onAddToListAction,
  onAdvancedSearchAction,
  onFieldBlur,
  onFieldFocus,
  onFieldInput,
  onFieldKeyDown,
  onFieldKeyUp,
  placeholder,
  propIsLoading,
  setDropdownOpen,
  setUserInput,
  stopFiltering,
  textAlign,
  userAssistanceDensity,
  userInput,
  virtualKeyboard
}: Props<K, D>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const { formFieldContext, inputProps, labelProps, textFieldProps, userAssistanceProps } =
    useTextField({
      ariaDescribedBy,
      helpSourceLink,
      helpSourceText,
      isDisabled: false,
      isFocused,
      isLoading,
      isReadonly: false,
      isRequiredShown: false,
      labelEdge: 'inside',
      styleVariant: 'default',
      value: ''
    });
  const { selectSingleFieldInputProps, selectSingleFieldProps } = useMobileDropdown({
    clearValue,
    displayValue,
    inputRef: mainFieldInputRef,
    isDropdownOpen: isOpen,
    setDropdownOpen,
    setUserInput,
    stopFiltering,
    userInput
  });

  // JET-54256 - inside label animates up on initial display with selected value
  // If we're waiting for the selected values to load, the component should look like it
  // has a value in the meantime.
  // Use the original propIsLoading instead of isLoading because we don't want to delay
  // the inside label position by a timer like we do the progressive loading indicator.
  formFieldContext.hasValue = propIsLoading || formFieldContext.hasValue;

  const mobileDropdownFieldLabelProps = {
    label: <Label {...labelProps}>{label}</Label>,
    labelEdge: 'inside' as const
  };

  const dropdownInlineUserAssistance =
    isOpen &&
    (assistiveText || helpSourceLink) &&
    (userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow') ? (
      <DropdownUserAssistance
        assistiveText={assistiveText}
        fieldLabel={label}
        helpSourceLink={helpSourceLink}
        helpSourceText={helpSourceText}
        userAssistanceDensity={userAssistanceDensity}
        {...userAssistanceProps}
      />
    ) : undefined;

  const dropdownListContent = isEmptyResults ? (
    isAddToListShown || isAdvancedSearchShown ? undefined : (
      <EmptyResults />
    )
  ) : (
    collectionRenderer(collectionRendererContext)
  );

  const addToListContent = isAddToListShown ? (
    <>
      <div onMouseDown={preventDefault}>
        <Separator />
      </div>
      <LinkItem onAction={onAddToListAction}>{translations.select_addToList()}</LinkItem>
    </>
  ) : undefined;

  const advancedSearchContent = isAdvancedSearchShown ? (
    <>
      <div onMouseDown={preventDefault}>
        <Separator />
      </div>
      <LinkItem onAction={onAdvancedSearchAction}>
        {translations.select_moreSearchOptions()}
      </LinkItem>
    </>
  ) : undefined;

  return (
    <SelectMobileDropdown
      header={
        <FormFieldContext.Provider value={formFieldContext}>
          <View>
            <SelectSingleField
              {...textFieldProps}
              {...mobileDropdownFieldLabelProps}
              {...selectSingleFieldProps}
              onFocus={onFieldFocus}
              onBlur={onFieldBlur}>
              <SelectSingleFieldInput
                {...inputProps}
                {...selectSingleFieldInputProps}
                aria-controls={dropdownId}
                aria-expanded={isOpen}
                aria-label={ariaLabel}
                isAddToListShown={isAddToListShown}
                isAdvancedSearchShown={isAdvancedSearchShown}
                isRequired={isRequired}
                liveRegionText={liveRegionText}
                placeholder={placeholder}
                textAlign={textAlign}
                virtualKeyboard={virtualKeyboard}
                onInput={onFieldInput}
                onKeyDown={onFieldKeyDown}
                onKeyUp={onFieldKeyUp}
              />
            </SelectSingleField>
          </View>
          {dropdownInlineUserAssistance}
          {addToListContent}
          {advancedSearchContent}
        </FormFieldContext.Provider>
      }
      hasHeaderSeparator={!(isAdvancedSearchShown && isEmptyResults)}
      id={dropdownId}
      isOpen={isOpen}
      labelId={labelProps.id}>
      <DropdownList
        isLoading={data === null}
        hasTopGap={!(isAddToListShown || isAdvancedSearchShown)}>
        {dropdownListContent}
      </DropdownList>
    </SelectMobileDropdown>
  );
}
